/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * PropTypes contract for props-driven syllabus upload.
 *
 * Upload vs form callbacks:
 * - `onSyllabusExtracted` — fired when SyllabusUploadCore finishes file extract/analysis.
 *   Payload shape: `{ id, uploadUrl, syllabusInfo }` from the upload pipeline.
 * - `onAssignmentCreate` / `onAssignmentUpdate` — inline form CRUD from list view
 *   (PCUX-43 split-pane). Parent persists to MobX schedule until publish.
 * - `onAssignmentSelect` — parent sets `selectedAssignmentId` and derives
 *   `assignmentFormMode` / `assignmentFormInitialValues` for inline EDIT (no modal).
 * - Loader, validation, and pipeline completion are parent-owned (one-reader plugin);
 *   child components do not receive `onValidationChange`.
 *
 * @file syllabusUploadContract.js
 * @author Satyaki Bose
 * @since 2.0.0
 */

import PropTypes from 'prop-types';
import {
  EMPTY_ASSIGNMENT_FORM_VALUES,
  ASSIGNMENT_FORM_MODE,
  ASSIGNMENT_SAVE_ACTION
} from '../components/assignments/assignmentFormActions';

export { ASSIGNMENT_SAVE_ACTION, ASSIGNMENT_FORM_MODE, EMPTY_ASSIGNMENT_FORM_VALUES };

export const assignmentListItemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  date: PropTypes.string,
  time: PropTypes.string,
  sections: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  menuItems: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    divider: PropTypes.bool
  }))
});

export const assignmentListTextInfoShape = PropTypes.shape({
  emptyStateTitle: PropTypes.string,
  emptyStateDescription: PropTypes.string,
  sectionsPrefix: PropTypes.string,
  notPublishedLabel: PropTypes.string,
  dragZoneLabel: PropTypes.string
});

export const syllabusUploadCallbackPropTypes = {
  onSyllabusExtracted: PropTypes.func,
  onAssignmentCreate: PropTypes.func,
  onAssignmentUpdate: PropTypes.func,
  onAssignmentDelete: PropTypes.func,
  onAssignmentSelect: PropTypes.func,
  onAssignmentEdit: PropTypes.func,
  onAssignmentCheckboxChange: PropTypes.func,
  onTabChange: PropTypes.func,
  onFileError: PropTypes.func
};

export const syllabusUploadListPropTypes = {
  showCheckboxes: PropTypes.bool,
  selectedAssignmentIds: PropTypes.arrayOf(PropTypes.string)
};

export const syllabusUploadCorePropTypes = {
  parentApp: PropTypes.string,
  rootHeight: PropTypes.string,
  useChannelsComponent: PropTypes.bool,
  env: PropTypes.string,
  locale: PropTypes.string,
  channelId: PropTypes.string,
  textbook: PropTypes.object,
  locationInApp: PropTypes.string,
  onStateChange: PropTypes.func,
  onScreenChange: PropTypes.func,
  onOfflineAction: PropTypes.func,
  onUploadSuccess: PropTypes.func,
  showSyllabusUploadTabs: PropTypes.bool,
  getUserCallback: PropTypes.func,
  textInfo: PropTypes.object,
  clientInformation: PropTypes.object,
  tocData: PropTypes.array,
  chapterMcqMapping: PropTypes.array
};

export const syllabusUploadDataPropTypes = {
  syllabusInfo: PropTypes.object,
  initialSyllabusInfo: PropTypes.object,
  /** Returns menu items applied to each assignment list row during parent mapping */
  getMenuItems: PropTypes.func,
  /** Parent-controlled AssignmentList row layout config */
  listItemConfig: PropTypes.object
};

export const syllabusUploadFormPropTypes = {
  assignmentFormMode: PropTypes.oneOf([ASSIGNMENT_FORM_MODE.CREATE, ASSIGNMENT_FORM_MODE.EDIT]),
  assignmentFormInitialValues: PropTypes.object,
  assignmentFormKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  assignmentConfig: PropTypes.object,
  restrictAlreadyAssigned: PropTypes.bool,
  existingAssignments: PropTypes.array,
  currentAssignmentIndex: PropTypes.number,
  formTextInfo: PropTypes.object,
  formActions: PropTypes.arrayOf(PropTypes.object)
};

export const DEFAULT_ASSIGNMENT_FORM_VALUES = EMPTY_ASSIGNMENT_FORM_VALUES;
